// Which side a picker panel (DatePicker, DateRangePicker) opens on.
//
// Inside a scrolling container — a Dialog is one — a panel that opens
// downward is clipped by the container's edge. The panel flips upward when it
// fits above instead; when it fits on neither side (a short dialog) it opens
// inline, in the flow under its field, so the container grows or scrolls and
// nothing is ever cut off. It stays in the DOM subtree on purpose: a portal
// would leave a modal Dialog's focus trap.

export type PopoverPlacement = 'top' | 'bottom' | 'inline'

type Band = { top: number; bottom: number }

/** Nearest ancestor that clips vertical overflow (else the viewport). */
function clipBandFor(element: HTMLElement): Band {
  let node = element.parentElement
  while (node && node !== document.body) {
    if (window.getComputedStyle(node).overflowY !== 'visible') {
      const rect = node.getBoundingClientRect()
      return { top: Math.max(rect.top, 0), bottom: Math.min(rect.bottom, window.innerHeight) }
    }
    node = node.parentElement
  }
  return { top: 0, bottom: window.innerHeight }
}

/** Below if it fits, else above if it fits, else inline. */
export function choosePopoverPlacement(
  trigger: Band,
  panelHeight: number,
  clip: Band,
): PopoverPlacement {
  const gap = 6
  if (clip.bottom - trigger.bottom - gap >= panelHeight) return 'bottom'
  if (trigger.top - clip.top - gap >= panelHeight) return 'top'
  return 'inline'
}

/**
 * Callback-ref helper: measures once when the panel mounts (before paint) and
 * records the side in `data-placement`, which `POPOVER_PANEL_PLACEMENT` turns
 * into position classes. The panel is positioned against its parent (the
 * picker's `relative` root, label included), so that is what is measured.
 * Measured once per opening so the panel never jumps while it is open.
 */
export function placePopover(panel: HTMLElement | null): void {
  const anchor = panel?.parentElement
  if (!panel || !anchor || panel.dataset.placement) return
  const placement = choosePopoverPlacement(
    anchor.getBoundingClientRect(),
    panel.offsetHeight,
    clipBandFor(anchor),
  )
  panel.dataset.placement = placement
  if (placement === 'inline') panel.scrollIntoView?.({ block: 'nearest' })
}

/** Position classes: below by default; above, or inline in the flow. */
export const POPOVER_PANEL_PLACEMENT =
  'top-full mt-1.5 data-[placement=top]:top-auto data-[placement=top]:bottom-full data-[placement=top]:mt-0 data-[placement=top]:mb-1.5 data-[placement=inline]:static'
